/**
 * WebAuthn ceremony helpers (EIP-305).
 *
 * These were duplicated byte-for-byte in PasskeySettings.jsx and LoginPage.js.
 * Centralised here so the encoding rules — which are easy to get subtly wrong
 * and fail only on some inputs — exist once.
 *
 * Kept dependency-free rather than pulling in @simplewebauthn/browser: that
 * package is ESM-only, and CRA's babel-jest ignores node_modules by default, so
 * adopting it means adding transformIgnorePatterns to the jest config. Not worth
 * it for ~40 lines. Revisit if we add conditional UI / autofill.
 */

/** True when the browser can run a WebAuthn ceremony at all. */
export function browserSupportsWebAuthn() {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function'
  );
}

/** base64url string -> ArrayBuffer. */
export function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return buffer;
}

/** ArrayBuffer -> base64url string (no padding). */
export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Server registration options -> PublicKeyCredentialCreationOptions.
 *
 * authenticatorAttachment is only forwarded when the server actually sent one.
 * The server deliberately omits it so a roaming security key is allowed as well
 * as a platform authenticator — passing `undefined` explicitly is treated as a
 * present-but-empty value by some browsers, which would defeat that.
 */
export function toCreationOptions(options) {
  const authenticatorSelection = {};
  if (options.authenticatorSelection?.authenticatorAttachment) {
    authenticatorSelection.authenticatorAttachment =
      options.authenticatorSelection.authenticatorAttachment;
  }
  if (options.authenticatorSelection?.residentKey) {
    authenticatorSelection.residentKey = options.authenticatorSelection.residentKey;
  }
  if (options.authenticatorSelection?.userVerification) {
    authenticatorSelection.userVerification =
      options.authenticatorSelection.userVerification;
  }

  return {
    challenge: base64urlToBuffer(options.challenge),
    rp: { id: options.rp.id, name: options.rp.name },
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      id: base64urlToBuffer(c.id),
      type: c.type,
    })),
    authenticatorSelection,
    attestation: options.attestation || 'none',
  };
}

/** Server authentication options -> PublicKeyCredentialRequestOptions. */
export function toRequestOptions(options) {
  return {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      id: base64urlToBuffer(c.id),
      type: c.type,
      // Omit rather than send null — an explicit null is a type error in the
      // WebAuthn API, and the server sends null when it has no transport hints.
      ...(c.transports?.length ? { transports: c.transports } : {}),
    })),
    userVerification: options.userVerification,
  };
}

/**
 * Registration credential -> JSON for the server.
 *
 * `transports` comes from getTransports() and is stored so later
 * authentication ceremonies can tell the browser where to look ("insert your
 * security key" vs "check your phone"). Guarded because it is not implemented
 * everywhere.
 */
export function serializeAttestation(credential) {
  let transports = [];
  try {
    transports = credential.response.getTransports?.() || [];
  } catch {
    transports = [];
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      transports,
    },
  };
}

/** Authentication assertion -> JSON for the server. */
export function serializeAssertion(credential) {
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      authenticatorData: bufferToBase64url(credential.response.authenticatorData),
      signature: bufferToBase64url(credential.response.signature),
      userHandle: credential.response.userHandle
        ? bufferToBase64url(credential.response.userHandle)
        : null,
    },
  };
}

/** Run the registration ceremony and return server-ready JSON. */
export async function createCredential(options) {
  const credential = await navigator.credentials.create({
    publicKey: toCreationOptions(options),
  });
  if (!credential) {
    throw new Error('No credential was created');
  }
  return serializeAttestation(credential);
}

/** Run the authentication ceremony and return server-ready JSON. */
export async function getAssertion(options) {
  const credential = await navigator.credentials.get({
    publicKey: toRequestOptions(options),
  });
  if (!credential) {
    throw new Error('No credential was returned');
  }
  return serializeAssertion(credential);
}

/** Canonical branded host for this deployment, or null when unknown. */
export function getCanonicalHostHint() {
  if (typeof window === 'undefined') return null;
  const { hostname } = window.location;
  if (!hostname.endsWith('.run.app')) return null;
  // Mirrors the nginx map in frontend/nginx.conf.template. Only used to make the
  // error message actionable — the redirect and the origin check are the real
  // controls, both server-side.
  return 'the branded insights.eva.vistry.ai address';
}

/**
 * Map a ceremony failure to something a human can act on.
 *
 * Pure and table-driven so it can be unit-tested without a DOM.
 *
 * SecurityError is the one people actually hit: it is what the browser raises
 * when the RP ID is not a registrable suffix of the page origin, i.e. when the
 * app was opened on the raw *.run.app host instead of the branded one.
 */
export function describeWebAuthnError(error) {
  if (!error) return 'Something went wrong. Please try again.';

  switch (error.name) {
    case 'NotAllowedError':
      return 'Passkey prompt was cancelled or timed out. Please try again.';
    case 'InvalidStateError':
      return 'This device already has a passkey registered for your account.';
    case 'SecurityError': {
      const hint = getCanonicalHostHint();
      return hint
        ? `Passkeys only work on ${hint}. Please sign in there instead.`
        : 'This page’s address is not allowed to use passkeys.';
    }
    case 'NotSupportedError':
      return 'This device does not support the required passkey type.';
    case 'AbortError':
      return 'Passkey prompt was interrupted. Please try again.';
    case 'ConstraintError':
      return 'Your device could not satisfy the security requirements (screen lock or biometrics may be disabled).';
    default:
      return (
        error.response?.data?.detail ||
        error.message ||
        'Could not complete the passkey step. Please try again.'
      );
  }
}
